import { Component } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { AlertController } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { timer } from 'rxjs';
import { ProfileModalPage } from './components/profile-modal/profile-modal.page';
import { DeviceDetectorService } from 'ngx-device-detector';
import {employeeMenu, patientMenu, scientistMenu} from './interfaces/menus';

@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html'
})

export class AppComponent {
  // declared variables
  public openDropdowns: { [key: string]: boolean } = {};
  public showSettings = false;
  public assets = environment.assets;
  public displayedVersion = localStorage.getItem('PSIAppVersion') || '1.2.0';
  public versionStatus = 'latest';
  public appPages = [];
  public isBrowserIE: boolean = false;
  public isDarkmode = JSON.parse(localStorage.getItem('darkMode') || 'false');
  public isMobile: boolean = false;

  constructor(
    public loadingController: LoadingController,
    public alertController: AlertController,
    private httpClient: HttpClient,
    private deviceService: DeviceDetectorService,
    public modalController: ModalController,
  ) {
    // Check if device is mobile
    this.checkMobile();
    // Listen for window resize to update mobile detection
    window.addEventListener('resize', () => {
      this.checkMobile();
    });
    // executes the function checkInternetExplorer(): checks if the browser is IE
    this.checkInternetExplorer();

    this.colorTest(this.isDarkmode);

    // check if application has a new version
    timer(0, 30000).subscribe(val => this.updateVersion());

    // check if user has a profile
    if (typeof localStorage.getItem('appMenu') == 'undefined' || localStorage.getItem('appMenu') == null) {
      this.profilePopup();
    } else {
      try {
        this.appPages = JSON.parse(localStorage.getItem('appMenu'));
      } catch {
        localStorage.removeItem('appMenu');
        this.profilePopup();
      }
    }
  }

  // opens the profile selector modal
  public selectProfile() {
    this.profilePopup(true);
    this.closeSettingsMenu();
  }

  // opens the edit menu modal
  public editMenu() {
    this.customizeMenu(0, false);
    this.closeSettingsMenu();
  }

  // opens the settings dropdown menu
  public openSettingsMenu() {
    this.showSettings = true;
  }

  // closes the settings dropdown menu
  public closeSettingsMenu() {
    this.showSettings = false;
  }

  // closes the dropdown menu of subpages
  public closeDropdownMenu() {
    this.openDropdowns = {};
  }

  // toggles a specific dropdown by page title
  public toggleDropdown(pageTitle: string) {
    this.openDropdowns[pageTitle] = !this.openDropdowns[pageTitle];
  }

  // checks if a dropdown is open
  public isDropdownOpen(pageTitle: string): boolean {
    return this.openDropdowns[pageTitle] || false;
  }

  // updates the version of application
  public updateVersion() {
    let oldVersion = localStorage.getItem('PSIAppVersion');
    this.httpClient.get('version.php?cache-bust=' + +Date.now(), { responseType: 'text' }).subscribe(newVersion => {
      if (newVersion != '') {
        if (typeof oldVersion == 'undefined' || oldVersion == null) {
          localStorage.setItem('PSIAppVersion', newVersion);
          oldVersion = newVersion;
          this.displayedVersion = newVersion;
          this.versionStatus = 'latest';
        }
        
        if (oldVersion != newVersion) {
          // Keep showing the currently running version until reload happens.
          this.displayedVersion = oldVersion;
          this.versionStatus = 'new update available';
          localStorage.setItem('PSIAppVersion', newVersion);
          this.loadingController.create({
            message: 'Update is being installed ...',
            duration: 3000
          }).then(loading => {
            loading.present();
            loading.onDidDismiss().then(() => window.location.reload());
          });
        } else {
          this.displayedVersion = newVersion;
          this.versionStatus = 'latest';
        }
      }
    }, error => { });
  }

  // profile selector popup
  async profilePopup(allowDismiss = false) {
    const alert = await this.alertController.create({
      header: 'Please choose a profile:',
      subHeader: 'Here you can select a predefined profile for the side menu. If you press "Custom View" then you can create your own profile. The profile can be changed at any time.',
      backdropDismiss: allowDismiss,
      buttons: [
        {
          text: 'Employee',
          handler: () => {
            localStorage.setItem('appMenu', JSON.stringify(employeeMenu));
            window.location.reload();
          }
        },
        {
          text: 'Scientist',
          handler: () => {
            localStorage.setItem('appMenu', JSON.stringify(scientistMenu));
            window.location.reload();
          }
        },
        {
          text: 'Patient',
          handler: () => {
            localStorage.setItem('appMenu', JSON.stringify(patientMenu));
            window.location.reload();
          }
        },
        {
          text: 'Custom View',
          handler: () => {
            this.customizeMenu();
          }
        }
      ]
    });
    await alert.present();
  }

  // modal to customize menu
  async customizeMenu(profile = 0, showBack = true) {
    const modal = await this.modalController.create({
      backdropDismiss: false,
      component: ProfileModalPage,
      componentProps: {
        profile: profile,
        showBack: showBack
      }
    });
    await modal.present();
    const { data } = await modal.onDidDismiss();
    if (data.change) {
      if (data.menu != null) {
        this.appPages = data.menu;
        localStorage.setItem('appMenu', JSON.stringify(data.menu));
      } else {
        this.profilePopup();
      }
    }
  }

  // checks if device is mobile (responsive to window size)
  private checkMobile() {
    // Check both device type and window width
    const isMobileDevice = this.deviceService.isMobile();
    const isSmallScreen = window.innerWidth <= 768;
    this.isMobile = isMobileDevice || isSmallScreen;
  }

  // checks if the browser is Internet Explorer
  private checkInternetExplorer() {
    var browserInfo = this.deviceService.getDeviceInfo().browser;
    if (browserInfo == 'IE' || browserInfo == 'MS-Edge') {
      this.isBrowserIE = true;
    }
  }

  private toogleDarkmode(event) {
    //let systemDark = window.addEventListener("(prefers-color-scheme: dark)", this.colorTest);

    //systemDark.addEventListener(AppComponent.colorTest);
    if(event.detail.checked){
      document.body.setAttribute('data-theme', 'dark');
      localStorage.setItem('darkMode', JSON.stringify(true));
    }
    else {
      document.body.setAttribute('data-theme', 'light');
      localStorage.setItem('darkMode', JSON.stringify(false));
    }
  }

  colorTest(isDarkmode) {
    console.log(isDarkmode);
    // Ensure we always set a theme - default to light if null/undefined
    const theme = (isDarkmode === true || isDarkmode === 'true') ? 'dark' : 'light';
    document.body.setAttribute('data-theme', theme);
    // Also ensure the isDarkmode property is set correctly
    this.isDarkmode = theme === 'dark';
  }
}
